/**
 * Camera utilities for the field-capture PWA.
 * Thin wrappers around getUserMedia and canvas capture.
 */

/**
 * Open the rear-facing camera at the sensor's native orientation and the
 * highest resolution it will negotiate. We don't constrain width/height to
 * portrait or landscape — mobile sensors are landscape-native and forcing
 * portrait dimensions just gets silently ignored on most devices. The
 * exported frame is whatever the device delivers, uncropped.
 */
export async function openRearCamera(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: { ideal: "environment" },
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
    audio: false,
  });
}

/**
 * Draw the current video frame to an off-screen canvas and return it as a
 * JPEG Blob at 0.9 quality.
 */
export async function captureFrame(video: HTMLVideoElement): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth || 1280;
  canvas.height = video.videoHeight || 720;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get 2D canvas context");
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("canvas.toBlob returned null"));
      },
      "image/jpeg",
      0.9,
    );
  });
}
